import { PageController } from '@client/components/_core/layout/PageController';
import { LandingPageSections } from '@client/components/_ui/display/landing-page-sections';
import { SystemAlertBanner } from '@client/components/admin/SystemAlertBanner';
import { useUser } from '@client/hooks/business/user/useUser';
import { getRoleDisplayName } from '@client/utils/roles/role-config';
import { ADMIN_ROLES, type AdminRole } from '@shared/constants/business/user';
import { createFileRoute } from '@tanstack/react-router';
import { Tag, Typography } from 'antd';
import { Shield } from 'lucide-react';
import { useAdminConsoleLandingItems } from './useAdminConsoleLandingItems';

export const Route = createFileRoute('/admin-console/')({
    staticData: {
        breadcrumb: {
            title: 'Home',
        },
        ui: {
            hideTitle: true,
            widthVariant: 'default',
            icon: Shield,
        },
    },
    component: AdminHome,
});

// ============================================================================
// Components
// ============================================================================

function WelcomeSection() {
    const user = useUser();
    const firstName = user.firstName || user.email?.split('@')[0] || 'Admin';

    // Get user's admin roles
    const adminRoles = user.roles.filter((role): role is AdminRole =>
        Object.values(ADMIN_ROLES).includes(role as AdminRole),
    );

    return (
        <div className="mb-6 overflow-hidden rounded-xl bg-linear-to-br from-accent-600 via-accent-500 to-accent-400 text-white shadow-lg">
            <div className="flex items-start justify-between p-6">
                <div>
                    <div className="mb-1 flex items-center gap-2 text-accent-100">
                        <Shield size={16} />
                        <span className="font-medium text-sm">Admin console</span>
                    </div>
                    <Typography.Title level={2} className="mb-2! text-white!">
                        Welcome back, {firstName}!
                    </Typography.Title>
                    <div className="flex flex-wrap gap-2">
                        {adminRoles.map((role) => (
                            <Tag key={role} className="border-white/30 bg-card/20 text-white" variant="filled">
                                {getRoleDisplayName(role)}
                            </Tag>
                        ))}
                    </div>
                </div>
                <div className="hidden opacity-20 sm:block" aria-hidden="true">
                    <Shield size={80} strokeWidth={1} />
                </div>
            </div>
            {/* Page description */}
            <div className="border-white/20 border-t bg-black/10 px-6 py-3">
                <Typography.Text className="text-sm text-white/80">
                    Manage platform-wide settings, user access, organisations, content libraries, and system
                    configuration.
                </Typography.Text>
            </div>
        </div>
    );
}

// ============================================================================
// Main Component
// ============================================================================

function AdminHome() {
    const sections = useAdminConsoleLandingItems();

    return (
        <PageController route={Route}>
            <SystemAlertBanner />
            <WelcomeSection />
            <LandingPageSections sections={sections} columns={3} />
        </PageController>
    );
}
